import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Rocket, TrendingUp, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const fadeUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 },
};

const engagementTypes = [
 {
  icon: Rocket,
  title: "Launch & Validation",
  subtitle: "For founders testing an idea",
  timeline: "3-6 weeks",
  description: "A focused sprint to validate your core assumptions and determine if your idea has real demand.",
  includes: [
   "Problem definition and assumption mapping",
   "Customer discovery interviews (10-20 conversations)",
   "Demand validation",
   "Pricing validation",
   "Minimum viable product (if appropriate)",
   "Go/no-go recommendation with data",
  ],
  outcomes: [
   "Clear understanding of customer problems",
   "Evidence of willingness to pay",
   "Validated or invalidated assumptions",
   "Confidence to move forward or pivot",
  ],
 },
 {
  icon: TrendingUp,
  title: "Scale & Product Support",
  subtitle: "For founders who have found traction",
  timeline: "Ongoing (monthly)",
  description: "Dedicated execution support to help you build, grow, and scale without burning out.",
  includes: [
   "Dedicated product execution",
   "Technical development support",
   "Weekly strategy and review calls",
   "Operational systems setup",
   "Growth strategy and execution",
   "Team and process guidance",
  ],
  outcomes: [
   "Sustainable product development pace",
   "Systems that scale",
   "Reduced operational burden",
   "Growth without chaos",
  ],
 },
];

// Two people at table working illustration
const illustration = "/images/engagement.png";

export default function EngagementTypesPage() {
 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       Engagement Types
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       Two ways to work together. one for testing ideas, one for scaling what works.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Engagement Cards */}
   <section className="bg-background py-16 sm:py-20 lg:py-24">
    <div className="container-wide">
     <div className="grid lg:grid-cols-2 gap-8">
      {engagementTypes.map((type, i) => (
       <motion.div
        key={type.title}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: i * 0.1 }}
       >
        <Card className="card-elevated h-full">
         <CardHeader className="pb-4">
          <div className="flex items-start justify-between mb-4">
           <div className="icon-container">
            <type.icon className="h-6 w-6 text-accent" />
           </div>
           <Badge variant="outline" className="flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            {type.timeline}
           </Badge>
          </div>
          <CardTitle className="font-serif text-2xl font-medium text-foreground">
           {type.title}
          </CardTitle>
          <CardDescription className="text-base">{type.subtitle}</CardDescription>
         </CardHeader>
         
         <CardContent className="space-y-5">
          <p className="text-muted-foreground">{type.description}</p>
          
          <div>
           <h4 className="font-medium text-sm text-foreground mb-3">What is Included</h4>
           <ul className="space-y-2">
            {type.includes.map((item, j) => (
             <li key={j} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
              <span className="text-sm text-foreground">{item}</span>
             </li>
            ))}
           </ul>
          </div>
          
          <Separator />
          
          <div>
           <h4 className="font-medium text-sm text-foreground mb-3">Outcomes</h4>
           <ul className="space-y-2">
            {type.outcomes.map((outcome, j) => (
             <li key={j} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{outcome}</span>
             </li>
            ))}
           </ul>
          </div>
         </CardContent>
        </Card>
       </motion.div>
      ))}
     </div>
    </div>
   </section>

   {/* How to Choose - With Illustration */}
   <section className="bg-section py-16 sm:py-20">
    <div className="container-wide">
     <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      {/* Content */}
      <motion.div
       initial={{ opacity: 0, x: -30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7 }}
      >
       <h2 className="heading-section text-foreground mb-8">How to Choose</h2>
       
       <div className="grid sm:grid-cols-2 gap-5">
        <div className="bg-card border border-border/50 rounded-xl p-5">
         <h3 className="font-serif text-lg font-medium text-foreground mb-3">Choose Launch & Validation if...</h3>
         <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• You have an idea but have not validated it</li>
          <li>• You are not sure if people will pay</li>
          <li>• You want clarity before committing resources</li>
          <li>• You are early-stage or pre-revenue</li>
         </ul>
        </div>
        
        <div className="bg-card border border-border/50 rounded-xl p-5">
         <h3 className="font-serif text-lg font-medium text-foreground mb-3">Choose Scale & Support if...</h3>
         <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• You have validated demand</li>
          <li>• You have paying customers</li>
          <li>• You need help executing and growing</li>
          <li>• You want ongoing partnership</li>
         </ul>
        </div>
       </div>
      </motion.div>

      {/* Illustration */}
      <motion.div
       initial={{ opacity: 0, x: 30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7, delay: 0.1 }}
       className="flex justify-center"
      >
       <div className="w-full max-w-sm">
        <img
         src={illustration}
         alt="Two people working together at a table"
         className="w-full h-auto"
        />
       </div>
      </motion.div>
     </div>
    </div>
   </section>

   {/* CTA */}
   <section className="bg-primary text-primary-foreground py-16 sm:py-20">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
     >
      <h2 className="heading-section mb-5">Not Sure Which Fits?</h2>
      <p className="body-base text-primary-foreground/80 mb-6">
       Tell us where you are. We will help you figure out the right path.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
       <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
        <Link to="/contact">
         Submit Your Wishlist
         <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
       </Button>
       <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
        <Link to="/pricing">See Pricing</Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
